import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed } from 'vue';
import type { IMatch } from '@/interface/IMatch';
import type { IOdds, IPriceOption } from '@/interface/IOdds';
import type { DisplaySection } from '@/interface/enum';
import { MarketType } from '@/interface/enum';
import {
    createSingleTicket,
    createSingleTicketKey,
} from '@/libraries/betSlip/ticketHelper';
import { useBetPage } from '@/libraries/composables/useBetPage';
import useOdds from '@/libraries/composables/useOdds';
import { createEuroTicket } from '@/libraries/euroTicket';
import { platform } from '@/libraries/helpers/platformHelper';
import { i18n } from '@/libraries/i18n';
import { isCorrectScoreMarket } from '@/libraries/oddsDisplay';
import PriceOptionLib from '@/libraries/priceOption';
import { formatMinDecimalPlace } from '@/libraries/utils/text';
import { strReverse } from '@/libraries/utils/utils';
import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
import { useParlayBetSlipStore } from '@/stores/betSlip/parlayBetSlipStore';
import { useSingleBetSlipStore } from '@/stores/betSlip/singleBetSlipStore';
import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

export interface UseOddsDisplayProps {
    matchInfo: Ref<IMatch>;
    oddsInfo: Ref<IOdds>;
    priceOption: Ref<IPriceOption>;
    displaySection: Ref<DisplaySection>;
    oddsComponentRef?: Ref<HTMLElement | null>;
}

export default function useOddsDisplay({
    matchInfo,
    oddsInfo,
    priceOption,
    displaySection,
    oddsComponentRef,
}: UseOddsDisplayProps) {
    const {
        oddRise,
        oddDrop,
        isOddsNegative,
        isOddsSuspended,
        isOddsSelected: isLegacyOddsSelected,
        point,
        minimumDecimalPlace,
        gaDataMatch,
        toFixedDecimalPoint,
    } = useOdds(matchInfo, oddsInfo, priceOption);

    const {
        isReverseDisplayOddsEnabled,
        isSingleModeBetSlipEnabled,
    } = storeToRefs(useFeatureToggleStore());

    const { isAsi, isMixParlayMode, marketPage } = storeToRefs(useRootStore());
    const { setTopScreenToast, clearTopScreenToast, setOddsPosition } = useRootStore();

    const { isShowBetSlip, isShowSingleBetSlip } = storeToRefs(useBetSlipStore());
    const { isTicketExist, removeTicket: removeSingleTicket, addTicket } = useSingleBetSlipStore();
    const {
        isTicketExist: isSingleModeTicketExist,
        addOrRemoveTicket: addOrRemoveSingleModeTicket,
    } = useSingleModeBetSlipStore();
    const {
        betSlip: mixParlayBetSlip,
    } = storeToRefs(useParlayBetSlipStore());
    const {
        addTicket: addParlayTicket,
        isTicketExist: isMixParlayTicketExist,
        removeTicket: removeMixParlayTicket,
        removeTicketOrBetSlip: removeParlayTicketOrBetSlip,
    } = useParlayBetSlipStore();

    const { betPage } = useBetPage(marketPage.value, displaySection.value);
    const singleModeTicketKey = computed(() => createSingleTicketKey(
        oddsInfo.value.oddsId,
        priceOption.value.option,
    ));
    const isOddsSelected = computed(() => {
        if (isMixParlayMode.value || !isSingleModeBetSlipEnabled.value) {
            return isLegacyOddsSelected.value;
        }
        return isSingleModeTicketExist(singleModeTicketKey.value);
    });

    const hasOption = computed(() => {
        const marketTypeList = [
            MarketType.CorrectScore,
            MarketType.FH_CorrectScore,
            MarketType.SH_CorrectScore,
            MarketType.TotalGoal,
            MarketType.HTFT,
            MarketType.FGLG,
        ];

        return marketTypeList.includes(oddsInfo.value.marketType);
    });

    // Helper functions
    function optionDisplay(optionName: string, marketType: MarketType) {
        if (isCorrectScoreMarket(marketType)) {
            const [key, home, away] = optionName.split(';');
            return i18n.t(key, { home, away });
        }

        if (MarketType.HTFT === marketType) {
            return PriceOptionLib.getEuroHtFtOption(optionName);
        }

        return i18n.t(optionName);
    }

    function showErrorMsg(messageKey: string) {
        setTopScreenToast({
            status: 'error',
            messageKey,
        });
    }

    function removeTicketsFromSameMatch() {
        Object.keys(mixParlayBetSlip.value.tickets)
            .filter(
                key => oddsInfo.value.eventId === mixParlayBetSlip.value.tickets[key].eventId,
            )
            .forEach((key) => {
                removeMixParlayTicket(key);
            });
    }

    async function handleMixParlayClick(key: string) {
        if (isMixParlayTicketExist(key)) {
            await removeParlayTicketOrBetSlip(key);
            setOddsPosition(0);
            return;
        }

        removeTicketsFromSameMatch();

        const newTicket = createEuroTicket(matchInfo.value, oddsInfo.value, priceOption.value, betPage.value, true);
        addParlayTicket(newTicket);
    }

    async function handleSingleBetClick(key: string) {
        if (isSingleModeBetSlipEnabled.value) {
            await handleSingleModeBetClick();
            return;
        }

        if (isTicketExist(key) && isShowSingleBetSlip.value) {
            await removeSingleTicket();
            setOddsPosition(0);
            return;
        }

        const isTicketElExisted = isShowBetSlip.value;

        const newTicket = createEuroTicket(matchInfo.value, oddsInfo.value, priceOption.value, betPage.value, false);
        addTicket(newTicket);
        updateOddsPosition(isTicketElExisted);
    }

    async function handleSingleModeBetClick() {
        const source = createEuroTicket(
            matchInfo.value,
            oddsInfo.value,
            priceOption.value,
            betPage.value,
            false,
        );
        const ticket = createSingleTicket(source);
        const isExistingTicket = isSingleModeTicketExist(ticket.key);
        await addOrRemoveSingleModeTicket(ticket, updateCurrentOddsPosition);
        if (isExistingTicket) {
            setOddsPosition(0);
        }
    }

    function updateCurrentOddsPosition() {
        if (!oddsComponentRef?.value) return;
        setOddsPosition(oddsComponentRef.value.getBoundingClientRect().y);
    }

    function updateOddsPosition(isTicketElExisted: boolean) {
        if (!oddsComponentRef?.value) {
            return;
        }

        if (isTicketElExisted) {
            updateCurrentOddsPosition();
            return;
        }

        setTimeout(() => {
            updateCurrentOddsPosition();
        }, 300);
    }

    const onClickOdds = async () => {
        if (!isAsi.value) {
            platform.showLogin('Sports');
            return;
        }

        clearTopScreenToast();
        if (isOddsSuspended.value) {
            showErrorMsg('betSlip_errorMessage_oddsClosed');
            return;
        }

        const key = `${oddsInfo.value.oddsId}-${priceOption.value.option}`;
        if (isMixParlayMode.value) {
            await handleMixParlayClick(key);
        } else {
            await handleSingleBetClick(key);
        }
    };

    return {
        oddRise,
        oddDrop,
        isOddsNegative,
        isOddsSuspended,
        isOddsSelected,
        point,
        minimumDecimalPlace,
        gaDataMatch,
        toFixedDecimalPoint,
        optionDisplay,
        hasOption,
        showErrorMsg,
        removeTicketsFromSameMatch,
        onClickOdds,
        formatMinDecimalPlace,
        strReverse,
        isReverseDisplayOddsEnabled,
    };
}
